/* Pre-scan pass — read the RFP's own response instructions before filling it.
 *
 * The app used to go straight from upload to column detection, so the system
 * prompt was byte-identical for every run of a given product regardless of what
 * the customer actually asked for. An RFP that mandates its own response
 * categories ("Native / Out of the Box", "Configuration Required", …) was
 * answered in Met/Partial/Gap anyway, which cannot be scored side by side
 * against other vendors.
 *
 * One cheap Claude call over the instruction-bearing cells fixes that. Every
 * field is optional: when nothing is detected the run falls back to the default
 * vocabulary, so a failed or empty pre-scan never blocks a fill.
 */

import XLSX from 'xlsx-js-style';
import { claudeProxy } from './claude-proxy.js';
import { DEFAULT_MODEL } from './models.js';
import { makeVocab } from './status-vocab.js';

const MAX_EXCERPT_CHARS = 60000;
const ROWS_PER_SHEET = 40;
const MAX_COLS = 12;
const CELL_CLAMP = 400;

/**
 * Flatten the workbook into a compact text excerpt: every sheet's header row
 * plus its first rows, with long cells clamped. Instruction text usually lives
 * either in a dedicated sheet or in an "Instructions" column on the first row.
 */
export function buildExcerpt(workbook) {
  const parts = [];
  let budget = MAX_EXCERPT_CHARS;

  for (const name of workbook.SheetNames) {
    if (budget <= 0) break;
    const ws = workbook.Sheets[name];
    if (!ws || !ws['!ref']) continue;
    const range = XLSX.utils.decode_range(ws['!ref']);
    const maxR = Math.min(range.e.r, ROWS_PER_SHEET);
    const maxC = Math.min(range.e.c, MAX_COLS);

    const lines = [`### SHEET: ${name}  (${range.e.r + 1} rows × ${range.e.c + 1} cols)`];
    for (let r = 0; r <= maxR; r++) {
      const cells = [];
      for (let c = 0; c <= maxC; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        const v = cell?.v;
        if (v == null) continue;
        let t = String(v).trim();
        if (!t) continue;
        if (t.length > CELL_CLAMP) t = t.slice(0, CELL_CLAMP) + '…';
        cells.push(`${XLSX.utils.encode_col(c)}: ${t}`);
      }
      if (cells.length) lines.push(`r${r} | ${cells.join(' | ')}`);
    }
    const block = lines.join('\n');
    parts.push(block.slice(0, budget));
    budget -= block.length;
  }

  return parts.join('\n\n');
}

const SYSTEM = [
  'You read RFP/RFI workbooks and extract the response instructions the issuer set out.',
  'You are NOT answering the RFP. You are only reporting what it tells respondents to do.',
  '',
  'Return ONLY a JSON object, no prose and no markdown fences:',
  '{',
  '  "response_vocabulary": [ { "label": "<exact category text>", "role": "met|partial|gap", "definition": "<the RFP\'s own definition, or \\"\\">" } ],',
  '  "word_limits": { "per_answer": <integer or null> },',
  '  "deadline": "<YYYY-MM-DD or null>",',
  '  "prohibited": [ "<thing respondents must not disclose>" ],',
  '  "notes": "<one or two sentences of anything else a respondent must obey>"',
  '}',
  '',
  'RULES',
  '1. response_vocabulary: ONLY populate this when the workbook explicitly mandates a set of',
  '   response categories (a legend, an instruction cell, or a data-validation list). Copy each',
  '   label EXACTLY as written, including capitalisation, spacing and punctuation.',
  '2. If the workbook does NOT mandate categories, return an empty array. Do not invent one, and',
  '   do not echo a generic Met/Partial/Gap set — an empty array is the correct answer.',
  '3. "role" maps each category to a traffic-light colour: "met" = requirement fully satisfied,',
  '   "gap" = not satisfied, "partial" = anything conditional (configuration, third-party,',
  '   custom development, roadmap).',
  '4. Every field is optional. Use null or an empty array when the workbook does not say.',
  '5. Never guess a deadline from a file name or a revision date.',
].join('\n');

/**
 * @returns {{ instructions: object|null, vocab: object|null, raw: object|null }}
 *   `vocab` is null unless the workbook mandated its own categories.
 */
export async function prescanWorkbook({ workbook, model = DEFAULT_MODEL, onLog = () => {} }) {
  const excerpt = buildExcerpt(workbook);
  if (!excerpt.trim()) {
    onLog('Pre-scan skipped — workbook produced no readable text', 'warn');
    return { instructions: null, vocab: null, raw: null };
  }

  onLog(`Pre-scan: reading response instructions from ${(excerpt.length / 1024).toFixed(1)} KB of workbook text…`, 'info');

  let text;
  try {
    text = await claudeProxy.complete({
      system: SYSTEM,
      messages: [{
        role: 'user',
        content: `Extract the response instructions from this RFP workbook.\n\n${excerpt}`,
      }],
      model,
      maxTokens: 2048,
    });
  } catch (e) {
    // Never block a fill on the pre-scan.
    onLog(`Pre-scan failed (${e.message}) — falling back to the default answer vocabulary`, 'warn');
    return { instructions: null, vocab: null, raw: null };
  }

  let raw;
  try {
    raw = parseJsonObject(text);
  } catch (e) {
    onLog(`Pre-scan returned unparseable JSON (${e.message}) — using the default answer vocabulary`, 'warn');
    return { instructions: null, vocab: null, raw: null };
  }

  const vocab = makeVocab(raw.response_vocabulary, 'rfp');
  const instructions = {
    word_limits: raw.word_limits && typeof raw.word_limits === 'object' ? raw.word_limits : null,
    deadline: typeof raw.deadline === 'string' ? raw.deadline : null,
    prohibited: Array.isArray(raw.prohibited) ? raw.prohibited.filter((x) => typeof x === 'string') : [],
    notes: typeof raw.notes === 'string' ? raw.notes.slice(0, 800) : '',
  };

  if (vocab) {
    onLog(`Pre-scan: this RFP mandates ${vocab.categories.length} response categories — ${vocab.categories.map((c) => c.label).join(' · ')}`, 'ok');
  } else {
    onLog('Pre-scan: no mandated response categories found — using Met / Partial / Gap', 'info');
  }

  return { instructions, vocab, raw };
}

/** Tolerant JSON object extractor — mirrors extractJsonArray in real-fill.js. */
export function parseJsonObject(text) {
  if (typeof text !== 'string') throw new Error('Non-string response');
  const s = text.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '');
  const start = s.indexOf('{');
  const end = s.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) throw new Error('No JSON object markers found');
  const candidate = s.slice(start, end + 1);
  try {
    return JSON.parse(candidate);
  } catch {
    return JSON.parse(candidate.replace(/,(\s*[\]}])/g, '$1'));
  }
}
